// Core/CardDataEngine.js
// Singleton — pure pub/sub. No cache ownership, no content knowledge.
// Envelope shape: { meta: { type, ver, target }, data: string }
// CardDataEngine never parses `data` — that's the concrete widget's job.
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';

const _name = 'CardDataEngine';

class CardDataEngine extends WssEventHandlerBase {
    _name = _name;
    _subscribers = new Map(); // routingKey -> Set<Subscriber>

    /**
     * bootup register to the necessary events from signalR
     */
    bootup = () => {
        // no-op — _name is already set as a class field, CoreBase has no initialize()
        super.bootup();

        this.cleanup();

        this._unsub = this._wssEventNotifier.subscribe('OnCardDataUpdated', this.onMessage);
    };

    /**
     * cleanup removes event handlers
     */
    cleanup() {
        super.cleanup();

        if (this._unsub) this._unsub();
    }

    /**
     * Builds the routing key from envelope meta fields.
     * @param {{type: string, ver: string, target: string|null}} meta
     * @returns {string}
     */
    _buildKey = (meta) => {
        let target = meta.target ?? '';
        target = target.toLowerCase();

        return `${meta.type}:${meta.ver}:${target}`;
    };

    /**
     * Subscribe to a (type, ver, target) combination. Subscriber must implement onData(envelope).
     * @param {{type: string, ver: string, target: string|null}} meta
     * @param {{onData: function}} subscriber
     */
    subscribe = (meta, subscriber) => {
        try {
            const key = this._buildKey(meta);
            if (!this._subscribers.has(key)) {
                this._subscribers.set(key, new Set());
            }
            this._subscribers.get(key).add(subscriber);
        } catch (error) {
            this.logError('subscribe', error.message);
        }
    };

    /**
     * Unsubscribe. Removes the key entirely if no subscribers remain.
     * @param {{type: string, ver: string, target: string|null}} meta
     * @param {{onData: function}} subscriber
     */
    unsubscribe = (meta, subscriber) => {
        try {
            const key = this._buildKey(meta);
            const set = this._subscribers.get(key);
            if (!set) return;
            set.delete(subscriber);
            if (set.size === 0) {
                this._subscribers.delete(key);
            }
        } catch (error) {
            this.logError('unsubscribe', error.message);
        }
    };

    /**
     * Called by CardDataHandler (PageContent) on every incoming envelope.
     * No parsing, no content knowledge — routes the raw envelope as-is.
     * @param {{meta: {type: string, ver: string, target: string|null}, data: string}} dto
     */
    onMessage = (dto) => {
        try {
            const key = this._buildKey(dto.meta);
            const set = this._subscribers.get(key);
            if (!set) return;

            set.forEach((subscriber) => subscriber.onData(dto));
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };
}

const cardDataEngine = new CardDataEngine();
export default cardDataEngine;
